// biome-ignore-all lint/a11y: TODO - address a11y issues later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { addDays, format, isBefore } from "date-fns";
import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  useWebsitePageMetricDrilldown,
  useWebsitePageMetrics,
} from "@/hooks/api/use-website-page-metrics";
import { formatDate, formatMonthYear } from "@/lib/date-formatting";

interface ProcessedData {
  data: Array<{
    rawDate: string;
    timestamp: number;
    pageViews: number;
    cumulative: number;
    uniqueVisitors?: number;
    cumulativeVisitors?: number;
  }>;
  firstEngagementDate: string | null;
  firstEngagementTimestamp: number | null;
  latestEngagementDate: string | null;
  latestEngagementTimestamp: number | null;
  firstPageViews: number;
  latestPageViews: number;
}

interface TimeSeriesDrilldown {
  date: string;
  engagement_count: number;
  unique_people?: number;
  cumulative_engagements?: number;
  cumulative_people?: number;
}

export function WebpageEngagementChart({
  webpageUrl,
  metric = "webpage_active_days",
  channelId,
  startDate,
  endDate,
}: {
  webpageUrl: string | null;
  metric?:
    | "days_since_first_engagement"
    | "days_since_latest_engagement"
    | "webpage_active_days";
  channelId?: string;
  startDate?: string;
  endDate?: string;
}) {
  const [showCumulative, setShowCumulative] = React.useState(false);

  const { data: drilldownResponse, isLoading } = useWebsitePageMetricDrilldown({
    url: webpageUrl,
    metric: metric,
    channelFilter: channelId,
    startDate,
    endDate,
    enabled: !!webpageUrl,
    limit: 1000,
  });

  const { data: metrics } = useWebsitePageMetrics({
    url: webpageUrl,
    metrics: [
      "days_since_first_engagement",
      "days_since_latest_engagement",
      "webpage_active_days",
      "first_engagement_date",
      "last_engagement_date",
    ],
    channelFilter: channelId,
    startDate,
    endDate,
    enabled: !!webpageUrl,
  });

  // Process the raw data
  const processedData = React.useMemo<ProcessedData>(() => {
    if (!drilldownResponse?.results?.length) {
      return {
        data: [],
        firstEngagementDate: null,
        firstEngagementTimestamp: null,
        latestEngagementDate: null,
        latestEngagementTimestamp: null,
        firstPageViews: 0,
        latestPageViews: 0,
      };
    }

    // Map the API data to our chart format
    const chartData = (
      drilldownResponse.results as unknown as TimeSeriesDrilldown[]
    ).map((item) => ({
      rawDate: item.date,
      timestamp: new Date(item.date).getTime(),
      pageViews: item.engagement_count,
      uniqueVisitors: item.unique_people,
      cumulativeEngagements: item.cumulative_engagements,
      cumulativeVisitors: item.cumulative_people,
    }));

    // Sort by date
    const sortedData = chartData.sort((a, b) => a.timestamp - b.timestamp);

    // Fill in missing dates with zero values
    const dateMap = new Map(sortedData.map((item) => [item.timestamp, item]));

    if (sortedData.length > 0) {
      const startDateObj = addDays(new Date(sortedData[0].rawDate), -5);
      const endDateObj =
        metric === "webpage_active_days"
          ? addDays(new Date(sortedData[sortedData.length - 1].rawDate), 7)
          : new Date();

      // Add entries for missing dates with zero page views
      for (
        let currentDate = new Date(startDateObj);
        isBefore(currentDate, endDateObj);
        currentDate = addDays(currentDate, 1)
      ) {
        const timestamp = currentDate.getTime();
        if (!dateMap.has(timestamp)) {
          dateMap.set(timestamp, {
            rawDate: format(currentDate, "yyyy-MM-dd"),
            timestamp,
            pageViews: 0,
            uniqueVisitors: 0,
            cumulativeEngagements: 0,
            cumulativeVisitors: 0,
          });
        }
      }
    }

    // Convert map back to array and sort chronologically
    const completeData = Array.from(dateMap.values()).sort(
      (a, b) => a.timestamp - b.timestamp,
    );

    // Find first and latest engagement dates
    const engagementDates = completeData.filter((d) => d.pageViews > 0);

    let firstEngagementDate: string | null = null;
    let firstEngagementTimestamp: number | null = null;
    let latestEngagementDate: string | null = null;
    let latestEngagementTimestamp: number | null = null;
    let firstPageViews = 0;
    let latestPageViews = 0;

    if (engagementDates.length > 0) {
      const firstEntry = engagementDates[0];
      const lastEntry = engagementDates[engagementDates.length - 1];

      firstEngagementDate = firstEntry.rawDate;
      firstEngagementTimestamp = firstEntry.timestamp;
      firstPageViews = firstEntry.pageViews;

      latestEngagementDate = lastEntry.rawDate;
      latestEngagementTimestamp = lastEntry.timestamp;
      latestPageViews = lastEntry.pageViews;
    }

    // Calculate cumulative values if not provided
    const finalData = completeData.reduce<
      Array<{
        rawDate: string;
        timestamp: number;
        pageViews: number;
        cumulative: number;
        uniqueVisitors?: number;
        cumulativeVisitors?: number;
      }>
    >((acc, curr, index) => {
      const prevCumulative = index > 0 ? acc[index - 1].cumulative : 0;
      const prevCumulativeVisitors =
        index > 0 ? acc[index - 1].cumulativeVisitors || 0 : 0;

      acc.push({
        ...curr,
        cumulative:
          curr.cumulativeEngagements || prevCumulative + curr.pageViews,
        cumulativeVisitors:
          curr.cumulativeVisitors ||
          prevCumulativeVisitors + (curr.uniqueVisitors || 0),
      });
      return acc;
    }, []);

    return {
      data: finalData,
      firstEngagementDate,
      firstEngagementTimestamp,
      latestEngagementDate,
      latestEngagementTimestamp,
      firstPageViews,
      latestPageViews,
    };
  }, [drilldownResponse, metric]);

  const {
    data,
    firstEngagementDate,
    firstEngagementTimestamp,
    latestEngagementDate,
    latestEngagementTimestamp,
    firstPageViews,
    latestPageViews,
  } = processedData;

  const calculateYAxisTicks = React.useMemo(() => {
    if (!data?.length) return [];

    // Calculate max value based on view mode
    const maxValue = showCumulative
      ? Math.max(...data.map((d) => d.cumulative))
      : Math.max(...data.map((d) => d.pageViews));

    // Round up to next multiple of 5 for cleaner divisions
    const roundedMax = Math.ceil(maxValue / 5) * 5;
    return Array.from({ length: 6 }, (_, i) =>
      Math.min(maxValue, Math.round((i * roundedMax) / 5)),
    );
  }, [data, showCumulative]);

  return (
    <div>
      <div className="pb-4 flex items-center justify-between">
        <h3 className="text-base font-medium">Page views over time</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">
              Show cumulative
            </span>
            <InfoTooltip
              trigger={
                <button
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoCircledIcon className="h-3 w-3" />
                </button>
              }
              side="top"
              className="w-[250px]"
            >
              <div className="text-sm">
                <h4 className="font-medium mb-1">Cumulative View</h4>
                <p className="text-muted-foreground">
                  Shows the total cumulative page views over time, helping you
                  understand the overall growth trend of webpage engagement.
                </p>
              </div>
            </InfoTooltip>
          </div>
          <Switch
            checked={showCumulative}
            onCheckedChange={setShowCumulative}
            aria-label="Toggle cumulative view"
            className="scale-75 data-[state=checked]:bg-primary/80"
          />
        </div>
      </div>
      {isLoading ? (
        <div className="flex h-[300px] w-full items-center justify-center">
          <Skeleton className="h-[300px] w-full" />
        </div>
      ) : !data?.length ? (
        <div className="flex h-[300px] w-full items-center justify-center">
          <p className="text-sm text-muted-foreground">No data available</p>
        </div>
      ) : (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 30, right: 30, left: 0, bottom: 10 }}
            >
              <CartesianGrid
                vertical={false}
                stroke="hsl(var(--muted))"
                opacity={0}
              />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatMonthYear}
                tick={{ fontSize: 12 }}
                minTickGap={30}
                type="category"
                domain={["auto", "auto"]}
                stroke="hsl(var(--muted-foreground))"
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12 }}
                stroke="hsl(var(--muted-foreground))"
                domain={[
                  0,
                  () => {
                    // Find the actual maximum value based on the view mode
                    const actualMax = showCumulative
                      ? Math.max(...data.map((d) => d.cumulative))
                      : Math.max(...data.map((d) => d.pageViews));

                    // Add padding for specific metrics
                    return metric === "days_since_first_engagement" ||
                      metric === "days_since_latest_engagement" ||
                      metric === "webpage_active_days"
                      ? Math.ceil(actualMax * 1.2)
                      : actualMax;
                  },
                ]}
                ticks={calculateYAxisTicks}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "0.5rem",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "0.75rem",
                  padding: "0.5rem",
                }}
                content={({ active, payload, label }) => {
                  if (!active || !payload) return null;

                  const date = new Date(label);
                  const formattedDate = formatDate(
                    date.toISOString().split("T")[0],
                  );

                  return (
                    <div className="bg-card p-2 rounded-md border shadow-sm">
                      <p className="font-semibold text-sm mb-1">
                        {formattedDate}
                      </p>
                      {payload.map((entry, index: number) => {
                        let displayLabel = "";
                        let displayColor =
                          entry.color || "hsl(var(--foreground))";

                        const dataKey = String(entry.dataKey || "");
                        const value = Number(entry.value || 0);
                        if (dataKey === "pageViews") {
                          displayLabel = "Daily page views";
                          displayColor = !showCumulative
                            ? "hsl(var(--primary))"
                            : "hsl(var(--muted-foreground))";
                        } else if (dataKey === "cumulative") {
                          displayLabel = "Total page views";
                          displayColor = "hsl(var(--primary))";
                        } else if (dataKey === "uniqueVisitors") {
                          displayLabel = "Unique visitors";
                          displayColor = "hsl(var(--chart-2))";
                        } else if (dataKey === "cumulativeVisitors") {
                          displayLabel = "Total unique visitors";
                          displayColor = "hsl(var(--chart-2))";
                        }

                        return (
                          <div
                            key={index}
                            className="flex items-center gap-2 text-xs"
                          >
                            <div
                              className="w-3 h-3 rounded-sm flex-shrink-0"
                              style={{ backgroundColor: displayColor }}
                            />
                            <span className="text-muted-foreground">
                              {displayLabel}:
                            </span>
                            <span
                              className="font-medium"
                              style={{ color: displayColor }}
                            >
                              {value}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                }}
              />
              {firstEngagementDate && firstEngagementTimestamp && (
                <ReferenceLine
                  x={firstEngagementTimestamp}
                  stroke="hsl(var(--ring))"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  yAxisId="left"
                  isFront={false}
                  segment={[
                    { y: Math.max(0, firstPageViews - 5) },
                    { y: firstPageViews + 10 },
                  ]}
                  label={({ viewBox }) => {
                    return (
                      <g>
                        <rect
                          x={viewBox.x - 50 + 35}
                          y={viewBox.y - 20}
                          width="100"
                          height="40"
                          fill="hsl(var(--background))"
                          rx="4"
                          className="drop-shadow-sm"
                        />
                        <text
                          x={viewBox.x + 35}
                          y={viewBox.y}
                          textAnchor="middle"
                          fill="hsl(var(--foreground))"
                          fontSize="12"
                        >
                          {firstEngagementDate
                            ? formatDate(firstEngagementDate)
                            : ""}
                        </text>
                        <text
                          x={viewBox.x + 35}
                          y={viewBox.y + 16}
                          textAnchor="middle"
                          fill="hsl(var(--muted-foreground))"
                          fontSize="11"
                        >
                          {metrics?.days_since_first_engagement} days ago
                        </text>
                      </g>
                    );
                  }}
                />
              )}
              {latestEngagementDate && latestEngagementTimestamp && (
                <ReferenceLine
                  x={latestEngagementTimestamp}
                  stroke="hsl(var(--ring))"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  yAxisId="left"
                  isFront={false}
                  segment={[
                    { y: Math.max(0, latestPageViews - 5) },
                    { y: latestPageViews + 10 },
                  ]}
                  label={({ viewBox }) => {
                    return (
                      <g>
                        <rect
                          x={viewBox.x - 50 - 35}
                          y={viewBox.y - 20}
                          width="100"
                          height="40"
                          fill="hsl(var(--background))"
                          rx="4"
                          className="drop-shadow-sm"
                        />
                        <text
                          x={viewBox.x - 35}
                          y={viewBox.y}
                          textAnchor="middle"
                          fill="hsl(var(--foreground))"
                          fontSize="12"
                        >
                          {latestEngagementDate
                            ? formatDate(latestEngagementDate)
                            : ""}
                        </text>
                        <text
                          x={viewBox.x - 35}
                          y={viewBox.y + 16}
                          textAnchor="middle"
                          fill="hsl(var(--muted-foreground))"
                          fontSize="11"
                        >
                          {metrics?.days_since_latest_engagement} days ago
                        </text>
                      </g>
                    );
                  }}
                />
              )}
              <Bar
                dataKey="pageViews"
                yAxisId="left"
                opacity={0.8}
                fill={
                  !showCumulative
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))"
                }
              />
              {showCumulative && (
                <Line
                  type="monotone"
                  dataKey="cumulative"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  yAxisId="left"
                />
              )}
              {/* Show unique visitors if available */}
              {metric === "webpage_active_days" &&
                data.some((d) => d.uniqueVisitors !== undefined) && (
                  <Line
                    type="monotone"
                    dataKey={
                      showCumulative ? "cumulativeVisitors" : "uniqueVisitors"
                    }
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    yAxisId="left"
                  />
                )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
